

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isAdmissionModalOpen, setIsAdmissionModalOpen] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);

  const heroSlides = [
    {
      id: 1,
      title: "Transform Your Career with Practical Skills",
      subtitle: "Industry-Ready Technical Education",
      description: "Join thousands of successful graduates who\'ve transformed their careers through our hands-on technical training programs. Get the practical skills employers demand.",
      video: "/assets/arial5.mp4",
      cta: "Explore Courses",
    },
    {
      id: 2,
      title: "State of the Art Learning Facilities",
      subtitle: "Modern Labs & Equipment",
      description: "Experience learning in our cutting-edge laboratories equipped with the latest technology and industry-standard equipment used by leading companies.",
      video: "/assets/arial5.mp4",
      cta: "Virtual Tour",
    },
    {
      id: 3,
      title: "Expert Staff with Industry Experience",
      subtitle: "Learn from the Best",
      description: "Our experienced faculty brings real-world expertise to the classroom, ensuring you learn not just theory but practical applications that matter in your career.",
      video: "/assets/arial5.mp4",
      cta: "Meet Faculty",
    },
    {
      id: 4,
      title: "Rhythm on Campus",
      subtitle: "Festival Energy",
      description: "A vibrant celebration of talent, music, and student joy that turns every corner of campus into an unforgettable experience.",
      image: "/assets/musicfest.jpeg",
      cta: "See Festival",
    },
    {
      id: 5,
      title: "Voices of the Future",
      subtitle: "Creative Expression",
      description: "Young artists, performers, and innovators share their stories through sound, creativity, and culture in a moment of collective inspiration.",
      image: "/assets/musicfest2.jpeg",
      cta: "Explore Talent",
    },
    {
      id: 6,
      title: "Lights, Sound, Community",
      subtitle: "Campus Celebration",
      description: "From stage lights to roaring crowds, our music festival captures the spirit of belonging, creativity, and unforgettable memories.",
      image: "/assets/musicfest3.jpeg",
      cta: "Join the Fun",
    },
    {
      id: 7,
      title: "Beyond the Classroom",
      subtitle: "Culture & Connection",
      description: "Education grows stronger when students connect, perform, and celebrate together through events that build confidence and community.",
      image: "/assets/musicfest4.jpeg",
      cta: "Celebrate Diversity",
    },
    {
      id: 7,
      title: "Office Administration Workshop",
      subtitle: "Hands-On Learning",
      description: "Administrative officers and Front Office staff from various organizations attended a workshop on office administration, learning practical skills to enhance their efficiency and effectiveness in the workplace.",
      image: "/assets/oaworkshop.jpeg",
      cta: "Learn More",
    }
  ];

  const getCubeStyle = (slideIndex) => {
    const rawDifference = slideIndex - currentSlide;
    const adjustedDifference = rawDifference > heroSlides.length / 2
      ? rawDifference - heroSlides.length
      : rawDifference < -heroSlides.length / 2
        ? rawDifference + heroSlides.length
        : rawDifference;

    const parallaxX = adjustedDifference * 18;
    const parallaxY = Math.abs(adjustedDifference) * 12;

    if (adjustedDifference === 0) {
      return {
        opacity: 1,
        transform: `translate3d(${parallaxX}px, ${parallaxY}px, 900px) scale(1.14) rotateY(0deg) rotateX(0deg)`,
        zIndex: 14,
        filter: 'blur(0px) saturate(1.2) brightness(1.02)',
        transformOrigin: 'center center',
      };
    }

    if (Math.abs(adjustedDifference) === 1) {
      return {
        opacity: 0.96,
        transform: `translate3d(${adjustedDifference * 260 + parallaxX}px, ${parallaxY}px, 260px) scale(0.92) rotateY(${adjustedDifference * -60}deg) rotateX(6deg)`,
        zIndex: 10,
        filter: 'blur(0px) saturate(1) brightness(0.82)',
        transformOrigin: 'center center',
      };
    }

    return {
      opacity: 0.28,
      transform: `translate3d(${adjustedDifference * 480 + parallaxX}px, ${parallaxY * 1.8}px, -100px) scale(0.72) rotateY(${adjustedDifference * -75}deg) rotateX(10deg)`,
      zIndex: 2,
      filter: 'blur(2px) saturate(0.9) brightness(0.65)',
      transformOrigin: 'center center',
    };
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      const transitionTimer = setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        setIsTransitioning(false);
      }, 3200);

      return () => clearTimeout(transitionTimer);
    }, 10000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // Check if modal has been shown before and show it on first visit
  useEffect(() => {
    const modalShown = localStorage.getItem('admissionModalShown');
    if (!modalShown) {
      // Delay showing modal by 1 second for better UX
      const timeoutId = setTimeout(() => {
        setIsAdmissionModalOpen(true);
        setHasShownModal(true);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const handleCloseModal = () => {
    setIsAdmissionModalOpen(false);
    // Set flag in localStorage so modal won't show again
    localStorage.setItem('admissionModalShown', 'true');
  };

  const nextSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
      setIsTransitioning(false);
    }, 5000);
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + heroSlides?.length) % heroSlides?.length);
      setIsTransitioning(false);
    }, 5000);
  };

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10">
      {/* Background Slides */}
      <div className="absolute inset-0 overflow-hidden perspective-[1800px]">
        {heroSlides?.map((slide, index) => (
          <div
            key={slide?.id}
            className="absolute inset-0 transition-all [transition-duration:3400ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
            style={{
              ...getCubeStyle(index),
              transformStyle: 'preserve-3d',
              willChange: 'transform, opacity, filter',
              backfaceVisibility: 'visible',
            }}
          >
            <div className="absolute inset-0 bg-black/40 z-10"></div>
            <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/50 via-transparent to-black/50"></div>
            <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.18),_transparent_60%)]"></div>
            {slide?.video ? (
              <video
                src={slide?.video}
                autoPlay
                muted
                loop
                className="w-full h-full object-cover scale-[1.08] motion-safe:animate-[cinematicPan_18s_ease-in-out_infinite_alternate]"
              />
            ) : (
              <img
                src={slide?.image}
                alt={slide?.title}
                className="w-full h-full object-cover scale-[1.08] motion-safe:animate-[cinematicPan_18s_ease-in-out_infinite_alternate]"
              />
            )}
          </div>
        ))}
      </div>
      
      {/* Content */}
      <div className={`relative z-20 h-full flex items-center transition-all [transition-duration:1400ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] ${isTransitioning ? 'opacity-100 translate-y-0' : 'opacity-85 translate-y-4'}`}>
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-accent/20 backdrop-blur-sm text-accent font-medium rounded-full text-sm">
                {heroSlides?.[currentSlide]?.subtitle}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {heroSlides?.[currentSlide]?.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
              {heroSlides?.[currentSlide]?.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/courses">
                <Button variant="default" size="lg" iconName="ArrowRight" iconPosition="right">
                  {heroSlides?.[currentSlide]?.cta}
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" iconName="Phone" iconPosition="left">
                  Schedule Visit
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Controls */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200"
        aria-label="Previous slide"
      >
        <Icon name="ChevronLeft" size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200"
        aria-label="Next slide"
      >
        <Icon name="ChevronRight" size={24} />
      </button>
      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 flex space-x-2">
        {heroSlides?.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors duration-400 ${
              index === currentSlide ? 'bg-white' : 'bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* New Admissions Popup Modal */}
      <Modal
        isOpen={isAdmissionModalOpen}
        onClose={handleCloseModal}
        title="🎓 New Admissions Open!"
        size="lg"
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="GraduationCap" size={48} className="text-primary" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">
              Join Karumo TTI Today!
            </h3>
            <p className="text-muted-foreground text-lg mb-4">
              Applications are now open for September intake. Take the first step towards transforming your career with industry-relevant technical education.
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-foreground mb-2">Why Choose Karumo TTI?</h4>
            <ul className="text-sm text-muted-foreground space-y-2 text-left">
              <li className="flex items-start">
                <Icon name="CheckCircle" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>TVET CDACC Accredited Programs</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircle" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>State-of-the-art Labs & Equipment</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircle" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Experienced Industry Trainers</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircle" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Strong Industry Partnerships</span>
              </li>
            </ul>
          </div>

          <a
            href="https://karumottiportal.ac.ke/Account/StudentApplication?returnUrl=%2FAppAdmin"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 w-full"
          >
            <Icon name="ExternalLink" size={20} className="mr-2" />
            Apply Now - Online Portal
          </a>

          <p className="text-xs text-muted-foreground mt-4">
            Applications close soon. Don't miss this opportunity!
          </p>
        </div>
      </Modal>
    </section>

    
  );
};

export default HeroSection;